import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, useTheme, useMediaQuery } from '@mui/material';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import { useTranslation } from '../../utils/translations';

export interface HorizontalScrollIndicatorProps {
    /**
     * Ref to the horizontally scrollable container element
     */
    containerRef: React.RefObject<HTMLDivElement>;
    /**
     * Scroll offset in pixels for each arrow click (default: 70% of container width)
     * @default undefined (auto-calculated to 70% of container width)
     */
    scrollAmount?: number;
    /**
     * Flag to show scroll indicators - useful for conditional rendering
     * @default true
     */
    show?: boolean;
    /**
     * Optional custom className for the wrapper
     */
    className?: string;
    /**
     * Optional custom sx prop for the wrapper
     */
    sx?: Record<string, unknown>;
    /**
     * Version key used to force re-binding when the target scroll element changes.
     */
    containerVersion?: number;
}

/**
 * HorizontalScrollIndicator Component - FIXED VERSION
 *
 * Key fixes:
 * 1. Buttons start as ENABLED (not disabled) by default
 * 2. Removed isScrolling locking mechanism that prevented clicks
 * 3. Simplified state management
 * 4. Added debug logging for troubleshooting
 * 5. Proper ref attachment timing with delay
 */
const HorizontalScrollIndicator: React.FC<HorizontalScrollIndicatorProps> = ({
    containerRef,
    scrollAmount: customScrollAmount,
    show = true,
    className,
    sx,
    containerVersion = 0,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Show only on mobile (xs) and tablet (sm) - hide on desktop (md and up)
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

    /**
     * Calculate scroll offset as 70% of container width for better UX
     */
    const getScrollOffset = useCallback(() => {
        if (customScrollAmount) return customScrollAmount;

        const container = containerRef.current;
        if (!container) return 200; // Fallback

        // Use 70% of visible container width as scroll offset
        return Math.max(Math.round(container.clientWidth * 0.7), 100);
    }, [containerRef, customScrollAmount]);

    /**
     * Check scroll position and update arrow visibility
     */
    const checkScrollPosition = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;

        // Can scroll left if not at the beginning
        const hasScrollableLeft = scrollLeft > 1;
        setCanScrollLeft(hasScrollableLeft);

        // Can scroll right if there's more content to the right
        const hasScrollableRight = scrollLeft + clientWidth < scrollWidth - 1;
        setCanScrollRight(hasScrollableRight);
    }, [containerRef]);

    /**
     * Handle scroll arrow click with smooth scroll
     */
    const handleArrowClick = useCallback(
        (direction: 'left' | 'right') => {
            const container = containerRef.current;
            if (!container) return;

            const scrollOffset = getScrollOffset();
            const currentScroll = container.scrollLeft;
            const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);

            const nextScroll =
                direction === 'right'
                    ? Math.min(currentScroll + scrollOffset, maxScroll)
                    : Math.max(currentScroll - scrollOffset, 0);

            // Already at boundary; avoid unnecessary scroll calls.
            if (Math.abs(nextScroll - currentScroll) < 1) {
                checkScrollPosition();
                return;
            }

            container.scrollTo({
                left: nextScroll,
                behavior: 'smooth',
            });

            checkScrollPosition();

            // Re-check scroll position after smooth scroll completes
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = setTimeout(() => {
                checkScrollPosition();
            }, 600); // Wait for smooth scroll to complete
        },
        [containerRef, getScrollOffset, checkScrollPosition],
    );

    /**
     * Initialize scroll detection on mount and when containerRef changes
     */
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Add delay to ensure DOM is fully rendered
        const initTimeoutId = setTimeout(() => {
            checkScrollPosition();
        }, 100);

        /**
         * Handle scroll events - update arrow visibility
         */
        const handleScroll = () => {
            checkScrollPosition();
        };

        /**
         * Handle resize events - recalculate scroll position
         */
        const handleResize = () => {
            checkScrollPosition();
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);

        return () => {
            clearTimeout(initTimeoutId);
            container.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [containerRef, checkScrollPosition, containerVersion]);

    // Don't render on desktop
    if (!isMobileOrTablet || !show) {
        return null;
    }

    const buttonSx = {
        position: 'absolute' as const,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 100,
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        borderRadius: '50%',
        border: `none`,
        padding: '8px',
        minWidth: '44px',
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease-in-out',
        boxShadow: theme.shadows[2],
        cursor: 'pointer !important',
        '&:hover:not(:disabled)': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: theme.shadows[4],
            transform: 'translateY(-50%) scale(1.08)',
        },
        '&:disabled': {
            opacity: 0.4,
            cursor: 'not-allowed !important',
            backgroundColor: theme.palette.action.disabled,
        },
        '&:active:not(:disabled)': {
            transform: 'translateY(-50%) scale(0.95)',
        },
    };

    return (
        <Box
            className={className}
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                zIndex: 9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                ...sx,
            }}
        >
            {/* Left Arrow */}
            <IconButton
                disabled={!canScrollLeft}
                onClick={() => handleArrowClick('left')}
                sx={{
                    ...buttonSx,
                    left: '8px',
                    pointerEvents: 'auto',
                }}
                aria-label={t('common.scrollLeftButton')}
                aria-disabled={!canScrollLeft}
                title={canScrollLeft ? t('common.scrollLeftButton') : 'Already at start'}
            >
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* Right Arrow */}
            <IconButton
                disabled={!canScrollRight}
                onClick={() => handleArrowClick('right')}
                sx={{
                    ...buttonSx,
                    right: '8px',
                    pointerEvents: 'auto',
                }}
                aria-label={t('common.scrollRightButton')}
                aria-disabled={!canScrollRight}
                title={canScrollRight ? t('common.scrollRightButton') : 'Already at end'}
            >
                <ChevronRightIcon sx={{ fontSize: 20 }} />
            </IconButton>
        </Box>
    );
};

export default HorizontalScrollIndicator;
